// [AI_START::module id="keep_session_root_user_fields" purpose="Stop by-id UserOutput writes from replacing session isRoot/roleId/teams when isRoot is already true" links=["cache_index", "useInternalUser"]]
/**
 * Session isRoot comes from getUser(self) + auth overlay, not from a by-id row.
 * makeUser and watchUsers fetch by id, so they send isRoot false and membership
 * teams/roleId. Apollo keys UserOutput by id and would otherwise overwrite the
 * session user while an agency root is switched into a dealer.
 *
 * This type merge runs on the entity only when UserOutput already has field
 * merge policies (teams, roomsLinks, and similar). Without those, Apollo writes
 * the by-id row first and readField sees isRoot false on both sides.
 */

type ReadField = (fieldName: string, from?: unknown) => unknown;

type MergeObjects = (existing: unknown, incoming: unknown) => unknown;

export type SessionRootUserFields = {
  isRoot?: boolean | null;
  roleId?: unknown;
  teams?: unknown;
};

// [AI_START::function id="shouldKeepSessionRootProjection" purpose="Detect the by-id poison write: isRoot true in store, false on incoming" links=["mergeUserOutputKeepingSessionRoot"]]
export function shouldKeepSessionRootProjection(
  existingIsRoot: boolean | null | undefined,
  incomingIsRoot: boolean | null | undefined
): boolean {
  return existingIsRoot === true && incomingIsRoot === false;
}
// [AI_END::shouldKeepSessionRootProjection]

// [AI_START::function id="keepSessionRootUserFields" purpose="Overlay session isRoot/roleId/teams onto an incoming user write" links=["shouldKeepSessionRootProjection"]]
export function keepSessionRootUserFields<T extends SessionRootUserFields>(
  existing: T | null | undefined,
  incoming: T
): T {
  if (shouldKeepSessionRootProjection(existing?.isRoot, incoming.isRoot)) {
    return {
      ...incoming,
      isRoot: existing!.isRoot,
      roleId: existing!.roleId,
      teams: existing!.teams,
    };
  }

  return incoming;
}
// [AI_END::keepSessionRootUserFields]

// [AI_START::function id="mergeUserOutputKeepingSessionRoot" purpose="Apollo UserOutput merge: resolve isRoot via readField, then keep session projection" links=["shouldKeepSessionRootProjection", "cache_index"]]
export function mergeUserOutputKeepingSessionRoot(
  existing: unknown,
  incoming: unknown,
  { mergeObjects, readField }: { mergeObjects: MergeObjects; readField: ReadField }
): unknown {
  if (!incoming || typeof incoming !== "object") {
    return mergeObjects(existing, incoming);
  }

  const existingIsRoot = existing
    ? (readField("isRoot", existing) as boolean | null | undefined)
    : undefined;
  const incomingIsRoot = readField("isRoot", incoming) as
    | boolean
    | null
    | undefined;

  if (shouldKeepSessionRootProjection(existingIsRoot, incomingIsRoot)) {
    return mergeObjects(existing, {
      ...incoming,
      isRoot: existingIsRoot,
      roleId: readField("roleId", existing),
      teams: readField("teams", existing),
    });
  }

  return mergeObjects(existing, incoming);
}
// [AI_END::mergeUserOutputKeepingSessionRoot]
// [AI_END::keep_session_root_user_fields]
